import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { tokenStore } from '@/lib/api/tokenStore'
import { useAuthStore } from '@/stores/authStore'
import { useVenueStore } from '@/stores/venueStore'

import { logout as requestServerLogout } from './api'

/**
 * 세션 정리의 **단일 구현** (설계 §2-2(6) A1·A2).
 *
 * 🚨 토큰만 지우면 안 된다 — React Query 캐시에 이전 계정의 구장 데이터가 남아 있어
 * 다른 계정으로 재로그인했을 때 잠깐 노출될 수 있다. 자발적 로그아웃과 비자발적 세션 만료가
 * **같은 정리 목록**을 쓰도록 여기 한 곳에 모은다.
 *
 * ⚠️ `deviceId` · `lastVenueId` 는 `tokenStore.clear()` 가 의도적으로 남긴다.
 */
export function clearSession(queryClient: QueryClient): void {
  tokenStore.clear()
  useAuthStore.getState().clearLoginId()
  useVenueStore.getState().clearCurrentVenue()
  queryClient.clear()
}

/**
 * 로그아웃 — **서버 세션 무효화 → 로컬 정리 → 로그인 화면** 순서 (설계 §2-1(f), D7).
 *
 * 🚨 서버 EP 를 **먼저, await 해서** 부른다. 토큰을 먼저 지우면 요청이 무인증(401)이 되어
 * 서버 `refresh_token` 행이 만료까지 살아남는다 — 로그아웃의 의미가 사라진다.
 * 🚨 서버 호출이 실패해도(네트워크·5xx·타임아웃) **로컬 정리와 이동은 무조건 수행한다.**
 * 사용자를 화면에 가두지 않는 쪽이 우선이다.
 *
 * 세션 만료(비자발적)는 `RootLayout` 이 `returnTo` 를 보존해 이동하지만,
 * 로그아웃(자발적)은 돌아올 곳을 남기지 않는다.
 *
 * @returns `[logout, isPending]` — 진행 중에는 버튼을 비활성화한다(R8).
 */
export function useLogout(): [() => Promise<void>, boolean] {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [isPending, setIsPending] = useState(false)

  const logout = useCallback(async () => {
    setIsPending(true)
    try {
      await requestServerLogout()
    } catch {
      // 서버가 못 지웠어도 사용자는 나갈 수 있어야 한다. 토큰은 아래에서 지운다.
    } finally {
      setIsPending(false)
      clearSession(queryClient)
      navigate('/login', { replace: true })
    }
  }, [queryClient, navigate])

  return [logout, isPending]
}
